"use client"

import { useState, useEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Search, BookOpen } from "lucide-react"
import Link from "next/link"
import { R2Image } from "@/components/r2-image"
import { bookPublicPath } from "@/lib/book-url"
import { trackSearch } from "@/lib/analytics"

interface Book {
  id: string
  slug?: string | null
  title: string
  author: string
  description?: string
  genre?: string
  coverUrl?: string
  summary_5mins?: string
  summary_15mins?: string
  summary_chapter?: string
  podcastUrl?: string
}

interface BooksResponse {
  books: Book[]
  total: number
  page: number
  totalPages: number
}

export function BookCatalog() {
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [genre, setGenre] = useState("all")
  const [page, setPage] = useState(1)
  const lastSearchTrackedRef = useRef<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      lastSearchTrackedRef.current = null
    }
  }, [debouncedSearch])

  // Fetch available genres from database
  const { data: genresData, isLoading: isLoadingGenres, error: genresError } = useQuery<{ genres: string[] }>({
    queryKey: ["genres"],
    queryFn: async () => {
      const response = await fetch("/api/genres")
      if (!response.ok) {
        throw new Error("Failed to fetch genres")
      }
      return response.json()
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  })

  const { data, isLoading, error } = useQuery<BooksResponse>({
    queryKey: ["books", { search, genre, page }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "12",
        ...(search && { search }),
        ...(genre && genre !== "all" && { genre }),
      })
      
      const response = await fetch(`/api/books?${params}`)
      if (!response.ok) {
        throw new Error("Failed to fetch books")
      }
      return response.json()
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
  }

  // Reset to page 1 when genre changes
  useEffect(() => {
    setPage(1)
  }, [genre])

  useEffect(() => {
    const q = debouncedSearch.trim()
    if (!q || isLoading || !data) return
    if (search.trim() !== q) return
    if (lastSearchTrackedRef.current === q) return
    lastSearchTrackedRef.current = q
    trackSearch({ query: q, results_count: data.total })
  }, [debouncedSearch, search, data, isLoading])

  // Use genres from database, fallback to empty array while loading or on error
  const genres = genresData?.genres || []

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load books. Please try again.</p>
      </div>
    )
  }

  return (
    <section className="relative py-12 sm:py-16 overflow-hidden">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-slate-50/50 to-background dark:via-slate-900/50"></div>
      
      {/* Decorative shapes */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-gradient-to-bl from-blue-400/10 to-cyan-400/10 dark:from-blue-600/10 dark:to-cyan-600/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-gradient-to-tr from-indigo-400/10 to-purple-400/10 dark:from-indigo-600/10 dark:to-purple-600/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center space-y-4 sm:space-y-6 mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 rounded-full border border-blue-200/50 dark:border-blue-500/30 backdrop-blur-sm mb-4">
            <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Hundreds of Books Available</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">
            Explore Our Vast Collection
          </h2>
          <p className="text-sm sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover hundreds of carefully curated books, expertly summarized and transformed into engaging podcasts. 
            From timeless classics to modern masterpieces, unlock the wisdom of great minds.
          </p>
        </div>

      {/* Search and Filters - Enhanced Design */}
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              placeholder="Search by title, author, or genre"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 pr-4 text-sm sm:text-base h-12 sm:h-14 rounded-xl border-2 border-slate-200 dark:border-slate-700 focus:border-blue-500 dark:focus:border-blue-500 shadow-sm bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm"
            />
          </div>
          <Button 
            type="submit" 
            size="lg"
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-300 h-12 sm:h-14 px-6 sm:px-8 rounded-xl"
          >
            <Search className="h-5 w-5 mr-2" />
            Search
          </Button>
        </form>

        <div className="flex gap-4 flex-wrap items-center justify-center">
          <div className="w-full sm:w-auto flex flex-col sm:flex-row sm:items-center gap-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm px-4 sm:px-6 py-3 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
            <span className="text-xs sm:text-sm font-semibold text-muted-foreground">Filter by Genre:</span>
            <Select value={genre} onValueChange={setGenre}>
              <SelectTrigger className="w-full sm:w-[220px] border-2 border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors rounded-lg bg-white dark:bg-slate-800">
                <SelectValue placeholder="🎭 All Genres" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">🎭 All Genres</SelectItem>
                {isLoadingGenres ? (
                  <SelectItem value="loading" disabled>
                    Loading genres...
                  </SelectItem>
                ) : genresError ? (
                  <SelectItem value="error" disabled>
                    Failed to load genres
                  </SelectItem>
                ) : genres.length > 0 ? (
                  genres.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-genres" disabled>
                    No genres available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Books Grid */}
      <div className="mt-8 sm:mt-12">
        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="animate-pulse h-full bg-gradient-to-br from-white/60 to-white/40 dark:from-slate-800/60 dark:to-slate-800/40 backdrop-blur-sm border-slate-200 dark:border-slate-700">
              <CardHeader>
                <div className="aspect-[3/4] bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded-lg" />
              </CardHeader>
              <CardContent>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {data?.books.map((book, index) => (
              <Link key={book.id} href={bookPublicPath(book)} className="block">
                <Card 
                  className="group hover:shadow-xl sm:hover:shadow-2xl hover:shadow-blue-500/10 dark:hover:shadow-blue-500/20 hover:scale-[1.02] sm:hover:scale-[1.03] transition-all duration-300 sm:duration-500 flex flex-col h-full cursor-pointer bg-gradient-to-br from-white via-white to-blue-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-blue-950/20 border border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 p-0 overflow-hidden animate-fadeIn"
                  style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'both' }}
                >
                  <div className="aspect-[2/3] sm:aspect-[3/4] relative overflow-hidden rounded-t-lg bg-muted">
                      {book.coverUrl ? (
                        <R2Image
                          src={book.coverUrl}
                          alt={book.title}
                          fill
                          className="object-contain object-center group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                          quality={90}
                          priority={false}
                          placeholder="blur"
                          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                          fallbackIcon={<BookOpen className="h-12 w-12 text-muted-foreground group-hover:text-primary transition-colors duration-300" />}
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center group-hover:bg-primary/10 transition-colors duration-300">
                          <BookOpen className="h-12 w-12 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
                        </div>
                      )}
                    </div>
                  <div className="p-3 sm:p-5 flex-1 flex flex-col">
                    <CardTitle className="text-sm sm:text-lg line-clamp-2 mb-1.5 sm:mb-2 group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                      {book.title}
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-1">
                      by {book.author}
                    </p>
                    {book.genre && (
                      <div className="flex flex-wrap gap-1">
                        {book.genre.split(',').map((g, idx) => (
                          <Badge key={idx} variant="secondary" className="text-[10px] sm:text-xs w-fit px-1.5 sm:px-2 group-hover:bg-gradient-to-r group-hover:from-blue-600/10 group-hover:to-indigo-600/10 group-hover:border-blue-300 dark:group-hover:border-blue-700 transition-all duration-300">
                            {g.trim()}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {/* Pagination - Enhanced */}
          {data && data.totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-12">
              <Button
                variant="outline"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </Button>
              <div className="px-6 py-2 bg-gradient-to-r from-blue-600/10 to-indigo-600/10 dark:from-blue-500/20 dark:to-indigo-500/20 rounded-xl border border-blue-200 dark:border-blue-800 backdrop-blur-sm">
                <span className="text-sm font-semibold">
                  Page {page} of {data.totalPages}
                </span>
              </div>
              <Button
                variant="outline"
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages}
                className="px-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
      </div>
    </div>
    </section>
  )
}

{/* Add fadeIn animation */}
<style jsx>{`
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fadeIn {
    animation: fadeIn 0.4s ease-out;
  }
`}</style>
